import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useBrand } from '../context/BrandContext'

export default function Login() {
  const { signIn, signInDemo } = useAuth()
  const { brand } = useBrand()
  const navigate = useNavigate()

  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole]         = useState('employee')
  const [error, setError]       = useState(null)
  const [loading, setLoading]   = useState(false)

  useEffect(() => {
    // Catch Supabase password reset email redirect
    const hash = window.location.hash
    if (hash && hash.includes('type=recovery')) {
      navigate('/reset-password' + hash)
    }
  }, [])
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('demo')) {
      const email    = sessionStorage.getItem('demo_email')
      const password = sessionStorage.getItem('demo_password')
      const role     = sessionStorage.getItem('demo_role') || 'employee'
      if (email && password) {
        sessionStorage.removeItem('demo_email')
        sessionStorage.removeItem('demo_password')
        sessionStorage.removeItem('demo_role')
        setRole(role)
        signIn(email, password).then(({ error }) => {
          if (!error) navigate(role === 'admin' ? '/admin' : '/dashboard')
        })
      }
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const { error } = await signIn(email, password)
    setLoading(false)
    if (error) { setError(error.message); return }
    navigate(role === 'admin' ? '/admin' : '/dashboard')
  }

  function handleDemo() {
    signInDemo(role)
    navigate(role === 'admin' ? '/admin' : '/dashboard')
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      fontFamily: "'Montserrat', sans-serif",
    }}>
      {/* Left panel */}
      <div style={{
        background: '#2B2B2B',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '3rem',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '2px solid #B8734A',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 700,
          color: '#B8734A', marginBottom: '1.5rem',
        }}>
          {brand.monogram || 'CS'}
        </div>
        <div style={{
          fontFamily: 'Georgia, serif', fontSize: '2.2rem',
          color: '#fff', fontWeight: 600, lineHeight: 1.2, marginBottom: '0.75rem',
        }}>
          {brand.productName || 'Collective Staff'}
        </div>
        <div style={{
          fontSize: 11, color: 'rgba(217,195,163,0.4)',
          textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '1.5rem',
        }}>
          {brand.tagline || 'Simplifying Work. Amplifying Results.'}
        </div>
        <div style={{ width: 40, height: 1, background: '#B8734A', marginBottom: '1.5rem' }} />
        <p style={{ fontSize: 13, color: 'rgba(247,242,235,0.5)', lineHeight: 1.8, maxWidth: 320 }}>
          Your team's complete HR workspace — onboarding, policies, timesheets, and more.
        </p>
        <div style={{ marginTop: 'auto', paddingTop: '3rem' }}>
          <div style={{ fontSize: 10, color: 'rgba(217,195,163,0.25)', letterSpacing: '0.1em' }}>
            GALLAGHER COLLECTIVE WORKS
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        background: '#F7F2EB',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '3rem',
      }}>
        <div style={{ maxWidth: 400, width: '100%', margin: '0 auto' }}>
          <div style={{
            fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em',
            color: '#B8734A', fontWeight: 600, marginBottom: '0.5rem',
          }}>
            Employee Portal
          </div>
          <h2 style={{
            fontFamily: 'Georgia, serif', fontSize: '1.8rem',
            color: '#2B2B2B', fontWeight: 600, marginBottom: '0.25rem',
          }}>
            Welcome back.
          </h2>
          <p style={{ fontSize: 12, color: '#6B7D6B', marginBottom: '2rem' }}>
            Sign in to {brand.companyName || 'Demo Company Co.'}
          </p>

          {/* Role toggle */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            border: '1px solid #EDE6DA', borderRadius: 8,
            overflow: 'hidden', marginBottom: '1.5rem',
          }}>
            {['employee', 'admin'].map(r => (
              <button key={r} onClick={() => setRole(r)} style={{
                padding: '10px',
                background: role === r ? 'rgba(184,115,74,0.08)' : '#fff',
                border: 'none',
                borderRight: r === 'employee' ? '1px solid #EDE6DA' : 'none',
                color: role === r ? '#B8734A' : '#6B7D6B',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Montserrat', sans-serif",
                textTransform: 'capitalize',
              }}>
                {r === 'employee' ? 'Employee' : 'Administrator'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block', fontSize: 10, textTransform: 'uppercase',
                letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 5,
              }}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                required
                style={{
                  width: '100%', padding: '10px 13px',
                  border: '1px solid #EDE6DA', borderRadius: 6,
                  fontSize: 13, fontFamily: "'Montserrat', sans-serif",
                  outline: 'none', color: '#2B2B2B', background: '#fff',
                }}
              />
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{
                display: 'block', fontSize: 10, textTransform: 'uppercase',
                letterSpacing: '0.1em', color: '#6B7D6B', fontWeight: 600, marginBottom: 5,
              }}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••••"
                required
                style={{
                  width: '100%', padding: '10px 13px',
                  border: '1px solid #EDE6DA', borderRadius: 6,
                  fontSize: 13, fontFamily: "'Montserrat', sans-serif",
                  outline: 'none', color: '#2B2B2B', background: '#fff',
                }}
              />
            </div>

            <div style={{ textAlign: 'right', marginBottom: '1.25rem' }}>
              <span
                onClick={() => navigate('/forgot-password')}
                style={{ fontSize: 11, color: '#B8734A', cursor: 'pointer' }}>
                Forgot password?
              </span>
            </div>

            {error && (
              <div style={{
                background: 'rgba(160,57,43,0.08)', border: '1px solid #A0392B',
                borderRadius: 6, padding: '8px 12px',
                fontSize: 12, color: '#A0392B', marginBottom: '1rem',
              }}>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '12px',
              background: loading ? '#8A9E8A' : '#B8734A',
              border: 'none', borderRadius: 6, color: '#fff',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 13, fontWeight: 600,
              cursor: loading ? 'default' : 'pointer',
              letterSpacing: '0.05em', marginBottom: '0.75rem',
            }}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <span
              onClick={handleDemo}
              style={{ fontSize: 12, color: '#B8734A', cursor: 'pointer' }}>
              Exploring? Load demo credentials →
            </span>
          </div>

          <div style={{
            textAlign: 'center', fontSize: 10,
            color: 'rgba(107,125,107,0.4)', marginTop: '2rem',
          }}>
            Powered by <span style={{ color: '#B8734A' }}>Collective Staff</span> · A Gallagher Collective Works Product
          </div>
        </div>
      </div>
    </div>
  )
}